<template>
  <v-container>
    <v-row>
      <v-col :cols="12" class="d-flex justify-space-between">
        <h1>{{ $t('texts.jobSearch') }}</h1>
        <div class="jobSearchItems text-xs-right" style="line-height: 42px">
          {{ searchResults.nbHits }}
          <template v-if="!$vuetify.breakpoint.xsOnly">
            {{ $t('texts.results') }}
          </template>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col :cols="12" :sm="5" :md="4" :lg="3">
        <v-card>
          <v-card-text class="pt-2">
            <v-text-field
              v-model="searchQuery"
              :label="$t('texts.insertJobtitle')"
              prepend-icon="search"
              hide-details
            />
          </v-card-text>
        </v-card>
        <v-expansion-panels
          v-model="searchFacetPanels"
          class="my-3"
          accordion multiple tile
        >
          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('texts.season') }} </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                v-for="(value, name, index) in facets.season"
                :key="index"
                @click="toggleFacet(`season:${name}`)"
              >
                <v-checkbox
                  :value="searchParameters.facetFilters.indexOf(`season:${name}`) >= 0"
                  :true-value="true"
                  :false-value="false"
                  color="primary"
                  readonly
                  class="mt-0"
                  hide-details
                >
                  <template v-slot:label>
                    {{ $t(`texts.${name}`) }} ({{ value }})
                  </template>
                </v-checkbox>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('texts.jobCategory.label') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                v-for="(value, name, index) in facets.job_category_id"
                :key="index"
                @click="toggleFacet(`job_category_id:${name}`)"
              >
                <v-checkbox
                  :value="searchParameters.facetFilters.indexOf(`job_category_id:${name}`) >= 0"
                  :true-value="true"
                  :false-value="false"
                  color="primary"
                  readonly
                  class="mt-0"
                  hide-details
                >
                  <template v-slot:label>
                    {{ $t(`texts.jobCategory.${name}`) }} ({{ value }})
                  </template>
                </v-checkbox>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('texts.jobPosition.label') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                v-for="(value, name, index) in facets.job_position_id"
                :key="index"
                @click="toggleFacet(`job_position_id:${name}`)"
              >
                <v-checkbox
                  :value="searchParameters.facetFilters.indexOf(`job_position_id:${name}`) >= 0"
                  :true-value="true"
                  :false-value="false"
                  color="primary"
                  readonly
                  class="mt-0"
                  hide-details
                >
                  <template v-slot:label>
                    {{ $t(`texts.jobPosition.${name}`) }} ({{ value }})
                  </template>
                </v-checkbox>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('texts.startMonth') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                v-for="(value, name, index) in facets.start_month"
                :key="index"
                @click="toggleFacet(`start_month:${name}`)"
              >
                <v-checkbox
                  :value="searchParameters.facetFilters.indexOf(`start_month:${name}`) >= 0"
                  :true-value="true"
                  :false-value="false"
                  color="primary"
                  readonly
                  class="mt-0"
                  hide-details
                >
                  <template v-slot:label>
                    {{ $t(`texts.months.${name}`) }} ({{ value }})
                  </template>
                </v-checkbox>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('texts.endMonth') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                v-for="(value, name, index) in facets.end_month"
                :key="index"
                @click="toggleFacet(`end_month:${name}`)"
              >
                <v-checkbox
                  :value="searchParameters.facetFilters.indexOf(`end_month:${name}`) >= 0"
                  :true-value="true"
                  :false-value="false"
                  color="primary"
                  readonly
                  class="mt-0"
                  hide-details
                >
                  <template v-slot:label>
                    {{ $t(`texts.months.${name}`) }} ({{ value }})
                  </template>
                </v-checkbox>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('texts.duration') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                v-for="(value, name, index) in facets.duration_in_months"
                :key="index"
                @click="toggleFacet(`duration_in_months:${name}`)"
              >
                <v-checkbox
                  :value="searchParameters.facetFilters.indexOf(`duration_in_months:${name}`) >= 0"
                  :true-value="true"
                  :false-value="false"
                  color="primary"
                  readonly
                  class="mt-0"
                  hide-details
                >
                  <template v-slot:label>
                    {{ name }} {{ $tc('texts.months.label', name) }} ({{ value }})
                  </template>
                </v-checkbox>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>{{ $t('texts.workload') }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                v-for="(value, name, index) in facets.workload_min"
                :key="index"
                @click="toggleFacet(`workload_min:${name}`)"
              >
                <v-checkbox
                  :value="searchParameters.facetFilters.indexOf(`workload_min:${name}`) >= 0"
                  :true-value="true"
                  :false-value="false"
                  color="primary"
                  readonly
                  class="mt-0"
                  hide-details
                >
                  <template v-slot:label>
                    {{ name }}% ({{ value }})
                  </template>
                </v-checkbox>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-container class="pa-0">
          <v-row>
            <v-col class="py-0">
              <a
                ref="https://www.algolia.com"
                target="_blank"
              >
                <img src="/static/logos/search-by-algolia.svg" height="18" style="margin-top: 5px;">
              </a>
            </v-col>
            <v-col class="py-0">
              <v-btn
                v-if="hasSearchParameters"
                small outlined color="primary"
                @click="resetSearchParameters()"
              >
                <v-icon>clear</v-icon>
                {{ $t('texts.resetFilter') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
      <v-col
        :cols="12" :sm="6" :md="7" :lg="8"
        :offset-sm="1" :offset-md="1"
      >
        <template v-if="results.length > 0">
          <job-list-item
            v-for="(item, index) in results"
            :key="`searchitem-${index}`"
            :job="item"
            :company="item.company.data"
          />
          <v-container
            class="ma-0 pa-0"
            v-if="numberOfPages > 0"
          >
            <v-row>
              <v-col class="d-flex justify-center">
                <v-btn
                  class="mr-1 pagination__button"
                  @click="changePage('before')"
                  :disabled="searchResults.page === 0"
                  depressed
                >
                  <v-icon>arrow_left</v-icon>
                </v-btn>
                <v-btn
                  v-for="item in numberOfPages"
                  :key="`page-${item}`"
                  class="mx-1 pagination__button"
                  @click="changePage(item)"
                  depressed
                  :color="item - 1 === searchResults.page ? 'primary' : ''"
                >
                  {{ item }}
                </v-btn>
                <v-btn
                  class="ml-1 pagination__button"
                  @click="changePage('next')"
                  :disabled="searchResults.page === numberOfPages - 1"
                  depressed
                >
                  <v-icon>arrow_right</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </template>
        <v-alert type="info" :value="hasSearchParameters && results.length === 0">
          <div class="d-flex justify-space-between align-center">
            <span>{{ $t('texts.noResults') }}</span>
            <v-btn
              small outlined color="white"
              @click="resetSearchParameters()"
            >
              <v-icon>clear</v-icon>
              {{ $t('texts.resetFilter') }}
            </v-btn>
          </div>
        </v-alert>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import algoliasearch from 'algoliasearch/lite';
import { algoliaAppId, algoliaApiKey } from '@/config';
import JobListItem from './searchlist/ListItems.vue';

const client = algoliasearch(algoliaAppId, algoliaApiKey);
const index = client.initIndex('jobs');

export default {
  name: 'Searchpanel',
  components: {
    JobListItem,
  },
  data() {
    return {
      client,
      index,
      searchResults: {
        nbHits: 0,
        page: 0,
        nbPags: 0,
        hitsPerPage: 0,
        hits: [],
        facets: {},
      },
      initialFacets: {
        season: {},
        job_category_id: {},
        job_position_id: {},
        start_month: {},
        end_month: {},
        duration_in_months: {},
        workload_min: {},
      },
      predefinedFacets: [],
      searchFacetPanels: [],
      searchQuery: '',
      searchParameters: {
        hitsPerPage: 10,
        facetFilters: [],
        page: 1,
      },
    };
  },
  created() {
    this.predefinedFacets = Object.keys(this.initialFacets).map((item) => item);
    this.getRouteQuery();
    this.search();
  },
  computed: {
    results() {
      return this.searchResults.hits;
    },
    numberOfPages() {
      return Math.ceil(this.searchResults.nbHits / this.searchResults.hitsPerPage);
    },
    facets() {
      return this.initialFacets;
    },
    hasSearchParameters() {
      const hasSearchQuery = this.searchQuery !== '';
      const hasFacetFilters = this.searchParameters.facetFilters.length > 0;
      return hasSearchQuery || hasFacetFilters;
    },
    triggerSearch() {
      return {
        searchQuery: this.searchQuery,
        ...this.searchParameters,
      };
    },
  },
  methods: {
    prepareFilter() {
      const { facetFilters } = this.searchParameters;
      if (facetFilters.length === 0) {
        return undefined;
      }

      // group by facet group
      const groupedFilter = {};
      facetFilters.forEach((item) => {
        const splittedItem = item.split(':');
        const group = splittedItem[0];
        if (!groupedFilter[group]) {
          groupedFilter[group] = [];
        }
        groupedFilter[group].push(item);
      });

      // create filter strings
      const preparedFilterParts = [];
      Object.keys(groupedFilter).forEach((group) => {
        const formattedOr = groupedFilter[group].join(' OR ');
        preparedFilterParts.push(`(${formattedOr})`);
      });

      const formattedFilter = preparedFilterParts.join(' AND ');

      return formattedFilter;
    },
    search() {
      const searchParams = {
        hitsPerPage: this.searchParameters.hitsPerPage,
        facets: this.predefinedFacets,
        page: this.searchParameters.page - 1,
        filters: this.prepareFilter(),
      };
      const requestOptions = {};
      this.index.search(this.searchQuery, searchParams, requestOptions).then((response) => {
        Object.assign(this.searchResults, response);
        if (!this.initialFacets.season.summer) {
          Object.assign(this.initialFacets, response.facets);
        }
      });
      this.setRouteQuery();
      this.isInitializing = false;
    },
    toggleFacet(facet) {
      const index = this.searchParameters.facetFilters.indexOf(facet);
      if (index >= 0) {
        this.searchParameters.facetFilters.splice(index, 1);
      } else {
        this.searchParameters.facetFilters.push(facet);
      }
    },
    setRouteQuery() {
      const queryParams = {
        page: this.searchParameters.page,
        query: this.searchQuery || undefined,
        facetFilters: this.searchParameters.facetFilters.length > 0
          ? this.searchParameters.facetFilters.join() : undefined,
      };
      this.$router.push({ query: queryParams }).catch(() => {});
    },
    getRouteQuery() {
      if (Object.keys(this.$route.query).length === 0) {
        return;
      }
      const {
        page,
        query,
        facetFilters,
        facetPanels,
      } = this.$route.query;

      // set page
      this.searchParameters.page = page || 1;

      // set query
      this.searchQuery = query || '';
      if (facetFilters) {
        const preparedFacetFilters = facetFilters.split(',');
        preparedFacetFilters.forEach((item) => {
          this.searchParameters.facetFilters.push(item);
        });
      }

      // set opened panels
      if (facetPanels) {
        const preparedFacetPanels = facetPanels.split(',');
        preparedFacetPanels.forEach((item) => {
          if (this.facetPanels.indexOf(parseInt(item, 10)) < 0) {
            this.facetPanels.push(parseInt(item, 10));
          }
        });
      }
    },
    toggleFacet(facet) {
      const index = this.searchParameters.facetFilters.indexOf(facet);
      if (index >= 0) {
        this.searchParameters.facetFilters.splice(index, 1);
      } else {
        this.searchParameters.facetFilters.push(facet);
      }
    },
    changePage(value) {
      if (!isNaN(value)) {
        this.searchParameters.page = value;
        return;
      }
      switch (value) {
        case 'first':
          this.searchParameters.page = 1;
          break;
        case 'before':
          this.searchParameters.page -= 1;
          break;
        case 'next':
          this.searchParameters.page += 1;
          break;
        case 'last':
          this.searchParameters.page = this.numberOfPages;
          break;
        default:
          this.searchParameters.page = 1;
          break;
      }
    },
    resetSearchParameters() {
      this.searchQuery = '';
      this.searchParameters.facetFilters = [];
    },
  },
  watch: {
    triggerSearch(newSearch, oldSearch) {
      this.search();
      if (newSearch.page !== oldSearch.page) {
        window.scrollTo(0, 0);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .pagination__button {
    min-width: 36px !important;
    padding: 0 !important;
  }
}
</style>
